/**
 * THE RECORDING FORMAT — one encoder for both recorders (Build 10b).
 *
 * Moved here from src/routes/Record.tsx, where Foredesk's SMS-link recorder
 * had it, because Kelvo's Questions page records too, and the engine
 * (workers/voice-notes.ts readWavHeader) accepts exactly the bytes this
 * function writes and nothing else. One encoder means the two recorders and
 * the engine's check can never disagree about the format: the engine's tests
 * feed it this function's own output.
 *
 * React-free and DOM-free, so it is tested directly (tests/voice-notes.test.ts).
 *
 * ==== WHY 16kHz PCM WAV ====
 * Speech recognition wants 16kHz mono. Capturing at the browser's native rate
 * (usually 48kHz) and resampling in software is the reliable path; FORCING
 * AudioContext({ sampleRate: 16000 }) produces silence on some hardware
 * (build standard §16.3). WAV is also the format least likely to surprise the
 * transcriber — no container negotiation, no codec suffix.
 */

export const TARGET_SAMPLE_RATE = 16000

/**
 * Flatten the captured frames, resample to 16kHz, and wrap as a 16-bit PCM WAV.
 *
 * Linear interpolation is enough here: speech at 48kHz downsampled to 16kHz has
 * no content near the new Nyquist limit that a listener or a recogniser would
 * miss, and a proper filter bank would be a lot of code protecting nothing.
 */
export function encodeWav(frames: Float32Array[], inputRate: number): ArrayBuffer {
  const total = frames.reduce((n, f) => n + f.length, 0)
  const flat = new Float32Array(total)
  let offset = 0
  for (const f of frames) {
    flat.set(f, offset)
    offset += f.length
  }

  const ratio = inputRate / TARGET_SAMPLE_RATE
  const outLength = Math.floor(flat.length / ratio)
  const resampled = new Float32Array(outLength)
  for (let i = 0; i < outLength; i++) {
    const position = i * ratio
    const low = Math.floor(position)
    const high = Math.min(low + 1, flat.length - 1)
    const weight = position - low
    resampled[i] = (flat[low] ?? 0) * (1 - weight) + (flat[high] ?? 0) * weight
  }

  // 44-byte canonical WAV header, then the samples.
  const buffer = new ArrayBuffer(44 + resampled.length * 2)
  const view = new DataView(buffer)
  const writeString = (at: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(at + i, text.charCodeAt(i))
  }

  writeString(0, 'RIFF')
  view.setUint32(4, 36 + resampled.length * 2, true)
  writeString(8, 'WAVE')
  writeString(12, 'fmt ')
  view.setUint32(16, 16, true) // PCM chunk size
  view.setUint16(20, 1, true) // format: PCM
  view.setUint16(22, 1, true) // channels: mono
  view.setUint32(24, TARGET_SAMPLE_RATE, true)
  view.setUint32(28, TARGET_SAMPLE_RATE * 2, true) // byte rate
  view.setUint16(32, 2, true) // block align
  view.setUint16(34, 16, true) // bits per sample
  writeString(36, 'data')
  view.setUint32(40, resampled.length * 2, true)

  let at = 44
  for (const sample of resampled) {
    // Clamp before scaling: a value outside [-1, 1] would wrap and produce a
    // loud click rather than clipping cleanly.
    const clamped = Math.max(-1, Math.min(1, sample))
    view.setInt16(at, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true)
    at += 2
  }
  return buffer
}
